import React, { Component } from 'react';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import cn from 'classnames';
import StudentAttendanceDetails from 'components/Admin/StudentAttendanceDetails';
import { apiErrorHandler } from 'core/errors';
import { API, getCancelToken, request } from 'redux/api';
import { pluralize } from 'utils';

import { ReactComponent as ChevronRightIcon } from 'assets/chevron_right.svg';

interface GetAllStudentAttendancesResponse extends MessageResponse {
  studentsData: StudentOverAllAverageAttendance[];
}

interface AllStudentAttendanceState {
  isLoading: boolean;
  students: StudentOverAllAverageAttendance[];
  studentBeingViewed: StudentOverAllAverageAttendance | null;
  showStudentAttendanceDetails: boolean;
}

type AllStudentAttendanceProps = ToastManagerProp;

class AllStudentAttendance extends Component<AllStudentAttendanceProps, AllStudentAttendanceState> {
  apiSources = getCancelToken();

  state: AllStudentAttendanceState = {
    students: [],
    isLoading: false,
    studentBeingViewed: null,
    showStudentAttendanceDetails: false,
  };

  async getAllStudentAttendances() {
    const response = await request<GetAllStudentAttendancesResponse>({
      url: API.ATTENDANCE_ADMIN_ALL_STUDENTS,
      cancelToken: this.apiSources.token,
    });
    return response.studentsData;
  }

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      this.setState({ isLoading: true });
      const students = await this.getAllStudentAttendances();
      this.setState({ students, isLoading: false });
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  showStudentAttendanceDetails = (student: StudentOverAllAverageAttendance) => {
    this.setState({
      showStudentAttendanceDetails: true,
      studentBeingViewed: student,
    });
  };

  getRow = (student: StudentOverAllAverageAttendance) => {
    return (
      <tr
        key={student.userId}
        className='hovered clickable'
        onClick={() => this.showStudentAttendanceDetails(student)}
      >
        <td className='table__cell'>
          <div className='table__cell-header table__cell-header--title'>{student.name}</div>
          <div className='table__cell-value'>{student.emailId}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>COURSES ENROLLED IN</div>
          <div className='table__cell-value'>{student.coursesEnrolledIn}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>AVERAGE ATTENDANCE</div>
          <div className={cn('table__cell-value', getColorClassForPercentage(student.averageAttendance))}>
            {student.averageAttendance}%
          </div>
        </td>
        <td className='table__cell table__cell--more'>
          <ChevronRightIcon display='block' />
        </td>
      </tr>
    );
  };

  render() {
    const { isLoading, students, studentBeingViewed, showStudentAttendanceDetails } = this.state;
    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {isLoading
              ? 'Loading attendance data...'
              : !students.length
              ? 'Attendance records not available for students yet'
              : `Attendance records available for ${students.length} enrolled ${pluralize(
                  students.length,
                  'student'
                )}`}
          </h3>
        </div>
        <div className='section__body'>
          <table className='table table--bordered'>
            <tbody>{this.state.students.map(this.getRow)}</tbody>
          </table>
        </div>
        <StudentAttendanceDetails
          isOpen={showStudentAttendanceDetails}
          student={studentBeingViewed}
          onClose={() => this.setState({ showStudentAttendanceDetails: false })}
        />
      </div>
    );
  }
}

/**
 * Returns a color class name for a number between 0 and 100, returns empty string otherwise
 * @param percentage attendance percentage
 */
const getColorClassForPercentage = (percentage: number) => {
  if (percentage >= 80) return 'table__cell-value--green';
  else if (percentage >= 60) return 'table__cell-value--orange';
  return 'table__cell-value--red';
};

export default withToastManager(AllStudentAttendance);
